/**
 * Route path patterns for the console, mirroring the route tree in
 * `src/Routes.tsx` (which composes them from relative segments — keep the two
 * in sync when adding or moving routes).
 *
 * These are the single source of truth for building links and typing params:
 *
 * - Build a URL with `generatePath(paths.groupDetails, { groupUuid })` —
 *   TypeScript checks the params object against the `:param` segments.
 * - Read params with `useRouteParams(paths.groupDetails)` (see
 *   `app/hooks/routing/useRouteParams`) so param names stay checked too.
 *
 * Note that `generatePath` URI-encodes each param value itself — do NOT wrap
 * values in `encodeURIComponent`, or reserved characters get encoded twice.
 */
export const paths = {
  onboarding: "/onboarding",
  overview: "/overview",
  activity: "/activity",
  jun0: "/jun0",

  // dashboards
  dashboards: "/dashboards",
  dashboardsCloud: "/dashboards/cloud",
  dashboardsCis: "/dashboards/cis",
  dashboard: "/dashboards/:key",
  dashboardSectionDetails: "/dashboards/:key/details/:sectionKey",

  // read-only details pages
  readonlyBindingDetails: "/bindings/:bindingUUID",
  readonlyAccountDetails: "/accounts/:accountKey",
  readonlyAccountGroupDetails: "/account-groups/:accountGroupUUID",
  readonlyPolicyCollectionDetails: "/policy-collections/:collectionId",

  // legacy /policies routes (each redirects to its /admin/policies twin)
  policies: "/policies",
  policyDetails: "/policies/:policyUUID",
  policyVersionDetails: "/policies/:policyUUID/version/:versionNumber",
  policyExecutionDetails: "/policies/:policyUuid/execution/:executionId",

  // cloudops
  cloudops: "/cloudops",
  resources: "/cloudops/resources",
  resourceDetails: "/cloudops/resources/:resourceKey",
  resourceExecutionDetails: "/cloudops/resources/:resourceKey/:executionId",

  // admin — accounts
  adminAccounts: "/admin/accounts",
  adminAccountAdd: "/admin/accounts/add",
  adminAccountDetails: "/admin/accounts/details/:accountProvider/:accountKey",
  adminAccountEdit: "/admin/accounts/details/:accountProvider/:accountKey/edit",

  // admin — policy collections (index redirects to the adminPolicies tab)
  adminPolicyCollections: "/admin/policy-collections",
  adminPolicyCollectionCreate: "/admin/policy-collections/create",
  adminPolicyCollectionDetails: "/admin/policy-collections/:collectionId",
  adminPolicyCollectionEdit: "/admin/policy-collections/:collectionId/edit",
  adminDpcCreate: "/admin/policy-collections/dynamic/create",
  adminDpcEdit: "/admin/policy-collections/dynamic/edit",

  // admin — account groups (index redirects to the adminAccounts tab)
  adminAccountGroups: "/admin/account-groups",
  adminAccountGroupCreate: "/admin/account-groups/create",
  adminAccountGroupDetails: "/admin/account-groups/:accountGroupUUID",
  adminAccountGroupEdit: "/admin/account-groups/:accountGroupUUID/edit",

  // admin — bindings
  adminBindings: "/admin/bindings",
  adminBindingCreate: "/admin/bindings/create",
  adminBindingDetails: "/admin/bindings/:bindingUUID",
  adminBindingEdit: "/admin/bindings/:bindingUUID/edit",
  adminBindingRunDetails: "/admin/bindings/:bindingUUID/run/:runID",

  // admin — policies
  adminPolicies: "/admin/policies",
  adminPolicyDetails: "/admin/policies/:policyUUID",
  adminPolicyVersionDetails:
    "/admin/policies/:policyUUID/version/:versionNumber",
  adminPolicyExecutionDetails:
    "/admin/policies/:policyUuid/execution/:executionId",

  // admin — repositories (index redirects to the adminPolicies tab)
  adminRepositories: "/admin/repositories",
  adminRepositoryAdd: "/admin/repositories/add",
  adminRepositoryDetails: "/admin/repositories/:repositoryUUID",
  adminRepositoryEdit: "/admin/repositories/:repositoryUUID/edit",
  adminRepositoryLegacyPolicies:
    "/admin/repositories/:repositoryUUID/legacy-policies",

  // notifications (index redirects to notificationsOverview)
  notifications: "/notifications",
  notificationsOverview: "/notifications/overview",
  notificationsSettings: "/notifications/settings",
  notificationTemplateAdd: "/notifications/settings/templates/add",
  notificationTemplateEdit: "/notifications/settings/templates/:name",
  notificationRules: "/notifications/rules",
  notificationRuleCreate: "/notifications/rules/create",
  notificationRuleDetails: "/notifications/rules/:ruleUUID",
  notificationRuleEdit: "/notifications/rules/:ruleUUID/edit",
  notificationGroupDetails: "/notifications/rules/:ruleUUID/issues/:groupId",

  // settings (index redirects to settingsProfile)
  settings: "/settings",
  settingsProfile: "/settings/profile",
  settingsProfileEdit: "/settings/profile/edit",
  settingsCost: "/settings/cost",

  // settings — integrations
  settingsIntegrations: "/settings/integrations",
  settingsIntegrationsSlack: "/settings/integrations/slack",
  settingsIntegrationsEmail: "/settings/integrations/email",
  settingsIntegrationsJira: "/settings/integrations/jira",
  settingsIntegrationsJiraCreate: "/settings/integrations/jira/create",
  settingsIntegrationsJiraEdit: "/settings/integrations/jira/:uuid/edit",
  settingsIntegrationsServiceNow: "/settings/integrations/service-now",
  settingsIntegrationsTeams: "/settings/integrations/teams",
  settingsIntegrationsTeamsSetup: "/settings/integrations/teams/setup",
  settingsIntegrationsTeamsChannelMapping:
    "/settings/integrations/teams/channel-mapping",

  // settings — personal API keys
  personalApiKeyCreate: "/settings/personal-api-keys/create",
  personalApiKeyEdit: "/settings/personal-api-keys/edit/:identity",

  // settings — access control
  accessControl: "/settings/access-control",
  identityProviders: "/settings/access-control/identity-providers",
  identityProviderAdd: "/settings/access-control/identity-providers/add",
  samlProviderDetails:
    "/settings/access-control/identity-providers/saml/:providerName",
  samlProviderEdit:
    "/settings/access-control/identity-providers/saml/:providerName/edit",
  cloudOrganizationDetails:
    "/settings/access-control/identity-providers/organization/:provider/:key",
  users: "/settings/access-control/users",
  userDetails: "/settings/access-control/users/:key",
  userEdit: "/settings/access-control/users/:key/edit",
  groups: "/settings/access-control/groups",
  groupAdd: "/settings/access-control/groups/add",
  groupDetails: "/settings/access-control/groups/:groupUuid",
  groupEdit: "/settings/access-control/groups/:groupUuid/edit",

  // legacy user details route (redirects to userDetails)
  legacyUserDetails: "/settings/users/:key",

  accessModel: "/settings/access-model",

  // settings — clouds
  settingsClouds: "/settings/clouds",
  gcpDetails: "/settings/clouds/gcp/details",
  gcpDeployment: "/settings/clouds/gcp/deployment",

  api: "/api",
} as const;

/** Any route path pattern from the registry. */
export type RoutePath = (typeof paths)[keyof typeof paths];
